import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Styled markdown renderer for lectures and rituals.
// Matches the hermetic parchment/ink aesthetic used across the app.
// Renders safely — never uses dangerouslySetInnerHTML on user-authored text.

const MD_COMPONENTS = {
  h1: ({ node, ...p }) => (
    <h1 className="font-display text-3xl md:text-4xl text-[#1A1918] tracking-tight leading-tight mt-10 mb-4" {...p} />
  ),
  h2: ({ node, ...p }) => (
    <h2 className="font-display text-2xl md:text-3xl text-[#1A1918] tracking-tight leading-snug mt-8 mb-3" {...p} />
  ),
  h3: ({ node, ...p }) => (
    <h3 className="font-display text-xl md:text-2xl text-[#1A1918] tracking-tight mt-6 mb-2" {...p} />
  ),
  h4: ({ node, ...p }) => (
    <h4 className="font-display text-lg text-[#1A1918] mt-5 mb-2" {...p} />
  ),
  p: ({ node, ...p }) => (
    <p className="font-body text-[16px] leading-[1.85] text-[#1A1918] my-4" {...p} />
  ),
  strong: ({ node, ...p }) => (
    <strong className="font-semibold text-[#1A1918]" {...p} />
  ),
  em: ({ node, ...p }) => (
    <em className="italic text-[#4A4238]" {...p} />
  ),
  ul: ({ node, ...p }) => (
    <ul className="list-disc list-outside pl-6 my-4 space-y-1.5 marker:text-[#8B3A3A]" {...p} />
  ),
  ol: ({ node, ...p }) => (
    <ol className="list-decimal list-outside pl-6 my-4 space-y-1.5 marker:text-[#8B3A3A]" {...p} />
  ),
  li: ({ node, ...p }) => (
    <li className="font-body text-[16px] leading-[1.7] text-[#1A1918]" {...p} />
  ),
  blockquote: ({ node, ...p }) => (
    <blockquote className="border-l-2 border-[#8B3A3A] pl-5 py-1 my-5 italic text-[#4A4238]" {...p} />
  ),
  hr: () => <hr className="my-8 border-0 border-t border-[#C1B49A]" />,
  a: ({ node, ...p }) => (
    <a
      className="text-[#8B3A3A] underline underline-offset-4 decoration-[#8B3A3A]/50 hover:decoration-[#8B3A3A] transition-colors"
      target="_blank"
      rel="noopener noreferrer"
      {...p}
    />
  ),
  code: ({ inline, className, children, ...p }) =>
    inline ? (
      <code className="font-mono text-[13px] bg-[#EAE0CA] px-1.5 py-0.5 border border-[#C1B49A]" {...p}>
        {children}
      </code>
    ) : (
      <pre className="font-mono text-[13px] bg-[#EAE0CA] border border-[#C1B49A] p-4 overflow-x-auto my-5">
        <code {...p}>{children}</code>
      </pre>
    ),
  table: ({ node, ...p }) => (
    <div className="my-5 overflow-x-auto">
      <table className="w-full border-collapse border border-[#C1B49A] text-[15px]" {...p} />
    </div>
  ),
  th: ({ node, ...p }) => (
    <th className="text-left border border-[#C1B49A] bg-[#EAE0CA] px-3 py-2 font-semibold text-[#1A1918]" {...p} />
  ),
  td: ({ node, ...p }) => (
    <td className="border border-[#C1B49A] px-3 py-2 align-top text-[#1A1918]" {...p} />
  ),
};

export default function MarkdownContent({ children, testId }) {
  return (
    <div data-testid={testId} className="max-w-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
        {children || ""}
      </ReactMarkdown>
    </div>
  );
}
